// -----------------------------------------------------------------------------
// Cierre de sesión por inactividad.
//
// Los 20 minutos configurados son la vida del ACCESS token, no de la sesión. El
// refresh token dura 7 días y la app lo renueva sola ante cualquier 401, así
// que en la práctica la sesión no se cerraba nunca: se podía dejar la pantalla
// abierta toda la noche y seguir dentro.
//
// Las dos cosas se necesitan y no se contradicen:
//   - la renovación silenciosa evita que a alguien lo echen a mitad de trabajo
//     (y es lo que arregló la conexión intermitente desde China);
//   - el cierre por inactividad evita que una sesión quede viva sola.
//
// Lo que las concilia es medir ACTIVIDAD DE LA PERSONA, no tráfico de red: si
// se contara cada petición, un refresco automático en segundo plano mantendría
// la sesión viva para siempre, que es justo lo que pasa hoy.
//
// La marca vive en localStorage y no en memoria para que cuente el tiempo con
// la pestaña cerrada o el computador suspendido: volver cuatro horas después
// debe pedir login de nuevo.
// -----------------------------------------------------------------------------

const CLAVE = "fe_ultima_actividad";

const MINUTOS_POR_DEFECTO = 20;

/** Minutos de inactividad tolerados antes de cerrar la sesión. */
export function minutosDeGracia() {
  const crudo = Number(process.env.REACT_APP_INACTIVIDAD_MIN);
  if (!Number.isFinite(crudo) || crudo <= 0) return MINUTOS_POR_DEFECTO;
  // Un mínimo de 1 minuto evita que un valor mal puesto deje la aplicación
  // inusable cerrando la sesión en cada clic.
  return Math.max(1, crudo);
}

function ahora() {
  return Date.now();
}

// localStorage puede fallar (modo privado, almacenamiento bloqueado). Si falla,
// se usa memoria: la sesión se sigue cerrando mientras la pestaña esté viva, y
// lo único que se pierde es contar el tiempo con la pestaña cerrada.
let respaldoEnMemoria = 0;

export function registrarActividad(cuando = ahora()) {
  respaldoEnMemoria = cuando;
  try {
    window.localStorage.setItem(CLAVE, String(cuando));
  } catch {
    // Se queda con el respaldo en memoria.
  }
}

export function ultimaActividad() {
  try {
    const guardado = Number(window.localStorage.getItem(CLAVE));
    if (Number.isFinite(guardado) && guardado > 0) return guardado;
  } catch {
    // Cae al respaldo.
  }
  return respaldoEnMemoria;
}

export function olvidarActividad() {
  respaldoEnMemoria = 0;
  try {
    window.localStorage.removeItem(CLAVE);
  } catch {
    // Nada que hacer.
  }
}

/** Milisegundos que faltan para que la sesión caduque. 0 = ya caducó. */
export function msRestantes(referencia = ahora()) {
  const ultima = ultimaActividad();
  // Sin marca no se cierra nada: es el primer arranque, o el almacenamiento se
  // limpió. Cerrar acá echaría a alguien que acaba de entrar.
  if (!ultima) return minutosDeGracia() * 60_000;
  const limite = ultima + minutosDeGracia() * 60_000;
  return Math.max(0, limite - referencia);
}

/** Si la sesión ya debe cerrarse por falta de actividad. */
export function sesionInactiva(referencia = ahora()) {
  return ultimaActividad() > 0 && msRestantes(referencia) === 0;
}

// Eventos que cuentan como "la persona está ahí". Deliberadamente NO se cuenta
// el tráfico de red: una pantalla que refresca sola no es alguien trabajando.
export const EVENTOS_DE_ACTIVIDAD = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
];
